import {
  getCredentialsByDomainUUID,
  getCredentialHistoryForCredentialUUID,
  getConfirmedTagsforIdentity,
  getCredentialUseScore,
  createNewIdentity,
  editIdentity,
} from '@/libraries/api/credentialMismatch'
import { fakeIentityMatchingGenderOfFace } from '@/libraries/util/demoData'
import * as flags from '@/libraries/util/flags'
import moment from 'moment'
import { Service } from '@/libraries/permissions'

const MAX_VERIFIED_CACHE_LENGTH = 5

export const CredentialMismatch = {
  namespaced: true,
  state: {
    identityIds: [],
    filteredIdentities: [],
    selectedAccessEvent: {},
    selectedCredAccessEvents: [],
    isLoading: false,
    taggingPanelVisible: false,
    currentIdentityIndex: undefined,
    selectedProfile: {},
    demoNames: {},
    credentialEventsCache: {},
    cachedCredentialQueie: [],
    verifiedDetectionsCache: {},
    cachedVerifiedDetectionsQueue: [],
    limitAccessEventHistory: true,
    submitInProgress: false,
  },
  getters: {
    identityIds: state => {
      return state.identityIds
    },
    isLoading: state => {
      return state.isLoading
    },
    currentIdentityIndex: state => {
      return state.currentIdentityIndex
    },
    sortedAccessEvents: state => {
      let sortedEvents = JSON.parse(JSON.stringify(state.selectedCredAccessEvents)).sort((accessEventA, accessEventB) => {
        const dateTimeFormat = 'YYYY-MM-DD HH:mm:ss'
        const momentA = moment.utc(accessEventA.eventTimestamp, dateTimeFormat)
        const momentB = moment.utc(accessEventB.eventTimestamp, dateTimeFormat)
        return momentB.valueOf() - momentA.valueOf()
      })
      return sortedEvents
    },
    taggingPanelVisible: state => {
      return state.taggingPanelVisible
    },
    selectedAccessEvent: state => {
      return state.selectedAccessEvent
    },
    selectedProfile: state => {
      return state.selectedProfile
    },
    selectedIdentityCotenants: state => {
      const filtered = state.identityIds
        .filter(identity => {
          const hasUnit = !!identity.unit
          const sameUnit =
            identity.unit === state.selectedProfile.unit
          const sameCredential =
            state.selectedProfile.identityId === identity.identityId
          return hasUnit && sameUnit && !sameCredential
        })
        .slice(0, 4)
      return filtered || []
    },
    filteredIdentities: state => {
      return state.filteredIdentities
    },
    verifiedDetectionsCache: state => {
      return state.verifiedDetectionsCache
    },
    verifiedMatchesForCurrentIdentity: state => {
      if (state.verifiedDetectionsCache.hasOwnProperty(state.selectedProfile.identityId)) {
        return state.verifiedDetectionsCache[state.selectedProfile.identityId]
      } else return []
    },
    cachedVerifiedDetectionsQueue: state => {
      return state.cachedVerifiedDetectionsQueue
    },
    limitAccessEventHistory: state => {
      return state.limitAccessEventHistory
    },
    submitInProgress: state => {
      return state.submitInProgress
    },
  },
  actions: {
    loadCredentialMismatchReport ({ state, commit, rootGetters, rootState }) {
      const currentDomainUUID = rootState.data.domains.current.uuid
      if (!currentDomainUUID) {
        console.debug(`Can't reload credentials with no domain.`)
        return
      }
      const canAccessService = rootGetters['entitlements/canAccessService']
      const canAccessKeyCard = canAccessService(Service.KEYCARD)
      if (!canAccessKeyCard) {
        return new Promise((resolve, reject) =>
          reject(Error(`Entitlements Error: Can't update credentials.`)),
        )
      }
      commit('incrementRequestCountForServiceNamed', 'credentials', {
        root: true,
      })
      return getCredentialsByDomainUUID(currentDomainUUID)
        .then(res => {
          if (!res) {
            throw new Error('credentials request has no response object.')
          }

          if (!res.data) {
            throw new Error('credentials request has no response data.')
          }

          if (!res.data.identities) {
            throw new Error('credentials request has no response data results.')
          }
          return res.data.identities
        })
        .then(data => {
          const mapped = data.map(identity => {
            const sorted = identity.credentials.sort((a, b) => {
              return moment.utc(b.last_seen, 'YYYY-MM-DD HH:mm:ss').valueOf() - moment.utc(a.last_seen, 'YYYY-MM-DD HH:mm:ss').valueOf()
            })
            identity.lastSeenUnixTimestamp = sorted.length > 0 ? moment.utc(sorted[0].last_seen, 'YYYY-MM-DD HH:mm:ss').valueOf() : undefined

            if (flags.DemoDataForFaces.enabled) {
              const identityId = identity.identityId
              let name = rootState.demoNames[identityId]

              if (!name) {
                const demo = fakeIentityMatchingGenderOfFace(identity)
                name = `${demo.firstName} ${demo.lastName}`
                commit('setDemoName', identityId, name)
              }
              identity.name = name
            }
            return identity
          })
          const sorted = mapped.sort((a, b) => {
            if (a.matchRatio < b.matchRatio) {
              return 1
            } else if (a.matchRatio > b.matchRatio) {
              return -1
            } else {
              return 0
            }
          })
          commit('addIdentities', sorted)
        })
        .catch(error => {
          console.log('Error: ', error)
        })
        .then(() => {
          commit('decrementRequestCountForServiceNamed', 'credentials', {
            root: true,
          })
        })
    },
    loadDetectionsForCredentialUUID ({ state, commit, rootState }, credentialUUID) {
      let params = [`domain_uuid=${rootState.data.domains.current.uuid}`]
      if (state.limitAccessEventHistory) {
        params.push(`limit=${state.limitAccessEventHistory}`)
      }
      return getCredentialHistoryForCredentialUUID(credentialUUID, params)
        .then(res => res.data)
        .then(data => {
          commit('addAccessEvents', data.access_events)
        })
    },
    loadVerifiedMatchesForIdentityId ({ state, commit, rootState }, identityId) {
      return getConfirmedTagsforIdentity(rootState.data.domains.current.uuid, identityId)
        .then(res => {
          if (res.data.ok) {
            if (res.data.confirmed_tags.length > 0) {
              const confirmedTags = res.data.confirmed_tags
              commit('addVerifiedTagsToCache', {
                identityId,
                confirmedTags,
              })
            } else console.log('Identity has no verified tags detected.')
          } else console.debug('Error loading verified matches into the store.') // TODO: send to bugsnag
        })
    },
    getUpdatedCredentialUseScore ({ state, commit, rootState }, incomingId) {
      const currentDomainUUID = rootState.data.domains.current.uuid
      getCredentialUseScore(currentDomainUUID, incomingId).then(res => {
        const index = state.identityIds.findIndex(identity => identity.identityId === incomingId)
        const updatedIdentityProfile = Object.assign({}, state.identityIds[index], {
          matchRatio: res.data.matchRatio,
          total: res.data.total,
        })
        commit('updateIdentityProfile', { index, identityProfile: updatedIdentityProfile })
      })
    },
    createNewIdentityProfile ({ state, commit, rootState }, identityProfile) {
      commit('setSubmittingFlag', true)
      createNewIdentity(identityProfile).then(function (result) {
        const newProfile = Object.assign({}, result.data.identityProfile)
        // TODO: index should be undefined
        const index = -1 // -1 in Array.splice() => end of array
        commit('updateIdentityProfile', { index, identityProfile: newProfile })
        commit('setSubmittingFlag', false)
      }).catch(error => {
        alert(error.message)
      })
    },
    editIdentityProfile ({ state, commit, rootState }, identityForSubmission) {
      const identityId = identityForSubmission.identityId
      const identityProfile = identityForSubmission
      commit('setSubmittingFlag', true)
      editIdentity(identityProfile, identityId).then(function (result) {
        const index = state.identityIds.findIndex(identity => identity.identityId === identityId)
        const newProfile = Object.assign({}, state.identityIds[index], result.data.profile)
        if (newProfile.deactivated) {
          commit('removeIdentityProfile', index)
        } else commit('updateIdentityProfile', { index, identityProfile: newProfile })

        commit('setSubmittingFlag', false)
      }).catch(error => {
        alert(error.message)
      })
    },
  },
  mutations: {
    addIdentities: (state, identityIds) => {
      state.identityIds = identityIds
    },
    addAccessEvents: (state, accessEvents) => {
      state.selectedCredAccessEvents = accessEvents
    },
    addVerifiedTagsToCache: (state, data) => {
      const identityIndex = state.cachedVerifiedDetectionsQueue.findIndex(id => id === data.identityId)
      if (identityIndex >= 0) {
        state.verifiedDetectionsCache[data.identityId] = data.confirmedTags
      } else {
        if (state.cachedVerifiedDetectionsQueue.length > MAX_VERIFIED_CACHE_LENGTH) {
          const removedIndentity = state.cachedVerifiedDetectionsQueue.shift()
          delete state.verifiedDetectionsCache[removedIndentity]
        }

        state.verifiedDetectionsCache = Object.assign({}, state.verifiedDetectionsCache,
          {
            [data.identityId]: data.confirmedTags,
          })
        state.cachedVerifiedDetectionsQueue.push(data.identityId)
      }
    },
    setSelectedAccessEvent: (state, accessEvent) => {
      state.selectedAccessEvent = accessEvent
    },
    setTaggingPanelVisibility: (state, taggingPanelVisibility) => {
      state.taggingPanelVisible = taggingPanelVisibility
    },
    setSelectedIdentityProfile: (state, profile) => {
      state.selectedProfile = profile
    },
    setCurrentIdentityIndex: (state, index) => {
      state.currentIdentityIndex = index
    },
    setDemoName: state => {
      return (uuid, name) => {
        state.demoNames[uuid] = name
      }
    },
    setFilteredIdentities: (state, newFilteredIdentities) => {
      state.filteredIdentities = newFilteredIdentities
    },
    setLimited: (state, limited) => {
      state.limitAccessEventHistory = limited
    },
    updateIdentityProfile: (state, payload) => {
      const deleteCount = payload.index > 0 ? 1 : 0
      state.identityIds.splice(payload.index, deleteCount, payload.identityProfile)
    },
    removeIdentityProfile: (state, index) => {
      state.identityIds.splice(index, 1)
    },
    setSubmittingFlag: (state, value) => {
      state.submitInProgress = value
    },
  },
}
